import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {Form, Row} from "../../../components/form/Form/Form";
import {MapMarkerField} from "../../../components/form/MapMarkerField/MapMarkerField";
import {getFilledFieldsObjOnFormSubmit, isErrorResponse, safelyParseJson} from "../../../utils/utils";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "../../../redux/Hooks/storeSelectors";
import {
    selectMapLayerIdByEntityName,
} from "../../../redux/selectors/selectors";
import {serviceApi} from "../../../api/serviceApi";
import {LoadingBackdrop} from "../../../components/LoadingBackdrop/LoadingBackdrop";
import {
    cleanRoadRouteCheckPoints,
    ROAD_ROUTE_CHECKER_MARKER_NAME
} from "../../../redux/road-route-check/road-route-check-reducer";
import './road-route-checker.scss';
import {
    PtElement,
    PtRouteType,
} from "../../../api/entities/replancity_RoadInfrastructureRoute";
import {useToastContext} from "../../../context/toastContext";
import {usePresetsContext} from "../../../context/presetsContext";
import {EntityServiceName} from "../../../api/enums/enums";
import * as TextField from "../../../components/form/TextField/TextField";
import {
    useMapMarkersCoordinates
} from "../../../project-modes/RoadNetworkMode/RoadNetworkManager/RoadRouteChecker/useMapMarkersCoordinates";
import {PublicTransitLayerEntityName} from "../../../api/entities/replancity_PublicTransitLine";
import RouteSegmentCard from "./RouteSegmentCard";
import {clearedSelectedMapFeatures} from "../../../redux/showInfo/showInfoReducer";
import {useMap} from "react-map-gl";
import {useMapFiltersContext} from "../../../context/mapFiltersContext";
import {colorBehavior, lineWidthExpression, pointStrokeBehavior} from "../../../utils/mapUtils";


const RouteChecker = ({entityServiceName, formFields}: {
    entityServiceName: EntityServiceName; formFields?: React.ReactNode;
}) => {
    const entityName = PublicTransitLayerEntityName.ROUTES;
    const routesLayerId = useTypedSelector((state) => selectMapLayerIdByEntityName(state, entityName));
    const [loading, setLoading] = useState<boolean>(false);
    const [routeDetails, setRouteDetails] = useState<PtRouteType>();
    const formRef = useRef<HTMLFormElement>(null);
    const {selectedPresetId: presetId} = usePresetsContext();
    const {markersCoordinates} = useMapMarkersCoordinates({
        fromPoint: ROAD_ROUTE_CHECKER_MARKER_NAME.fromPoint,
        toPoint: ROAD_ROUTE_CHECKER_MARKER_NAME.toPoint
    });
    const {mapboxMapId} = useMap();
    const {setLayerPaint} = useMapFiltersContext();
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {addToast} = useToastContext();

    useEffect(() => {
        return () => {
            dispatch(cleanRoadRouteCheckPoints());
        }
    }, []);

    useEffect(() => {
        return () => {
            setLayerPaint(prevState => {
                    const {[routesLayerId]: toRemove1, ...rest} = prevState;
                    return {
                        ...rest,
                    }
                }
            )

            dispatch(clearedSelectedMapFeatures());
        }
    }, [mapboxMapId]);

    const onFormSubmit = useCallback(async (event) => {
        const abortController = new AbortController();

        const formDataObj = getFilledFieldsObjOnFormSubmit(event);

        const fromPoint = JSON.parse(formDataObj.fromPoint as string);
        const toPoint = JSON.parse(formDataObj.toPoint as string);

        setLoading(true);

        const resp = await serviceApi.calculateAndGetPtRouteGeometry(
            entityServiceName,
            {
                presetId,
                fromLon: fromPoint.lng,
                fromLat: fromPoint.lat,
                toLon: toPoint.lng,
                toLat: toPoint.lat,
                startTime: formDataObj.startTime as string
            } as any, abortController.signal);

        if (isErrorResponse(resp)) {
            try {
                const parsedErr = typeof resp.error === 'string' ? safelyParseJson(resp.error)?.error : resp.error.error;
                addToast(parsedErr, 'error');
            } catch (e) {
                console.log('Replan. Error parsing error response', resp, e);
            }
        } else {
            const {elements} = resp;
            const featureStateIds = elements
                ?.filter((el): el is PtElement => el.mode === 'pt')
                ?.flatMap(({featureStateIdsList}) => featureStateIdsList);

            setRouteDetails(resp);

            const routeFeatureExpression = ['in', ['get', 'featureStateId'], ['literal', featureStateIds]];
            setLayerPaint(prevState => ({
                ...prevState,
                [routesLayerId]: {
                    'line-width': ['case', routeFeatureExpression, 5, 1],
                    'line-opacity': ['case', routeFeatureExpression, 1, 0.7],
                    'line-color': ['case', routeFeatureExpression, colorBehavior, 'grey']
                },
            }))
        }

        setLoading(false);

        return;
    }, [entityName, presetId, routesLayerId])

    return (
        <div className="road-route-checker">
            <div className="road-route-checker__form">
                <LoadingBackdrop isVisible={loading} transparent>
                    <div className="entity-editor__header">
                        <div className="title">
                            {t('route-check.header-title')}
                        </div>
                    </div>
                    {
                        markersCoordinates.fromPoint && markersCoordinates.toPoint
                            ? <Form
                                name="entityForm"
                                onSubmit={onFormSubmit}
                                submitBtnTitle={t('route-check.form-submit-btn')}
                                ref={formRef}
                                submitBtnTestId="entityFormSubmitBtn"
                                testId="entityForm"
                            >
                                <Row>
                                    <MapMarkerField
                                        name={ROAD_ROUTE_CHECKER_MARKER_NAME.fromPoint}
                                        defaultCoordinates={markersCoordinates.fromPoint}
                                        fieldGroupClassName="map-marker-form-field"
                                    />
                                    <MapMarkerField
                                        name={ROAD_ROUTE_CHECKER_MARKER_NAME.toPoint}
                                        defaultCoordinates={markersCoordinates.toPoint}
                                        fieldGroupClassName="map-marker-form-field"
                                    />
                                </Row>
                                <Row>

                                    <TextField.default
                                        name="startTime"
                                        // value={"09:00:00"}
                                        label={t('transit-lines.departure-from')}
                                        type="time"
                                        step="1"
                                        required={true}
                                        style={{width: 'maxContent'}}
                                        autoFocus
                                    />
                                </Row>
                            </Form>
                            : null
                    }
                </LoadingBackdrop>
            </div>
            <div className="road-route-checker__result">
                {
                    routeDetails?.isFromCache
                        ? <div className="segment-card__line">
                            Route has been calculated using cache and may not
                            contain latest changes, cache live time is 3 min after last routing request!
                        </div>
                        : null
                }
                {
                    routeDetails?.elements?.length
                        ? (
                            <div className="road-route-checker__segments">
                                {
                                    routeDetails.elements.map((segment, ind) => (
                                        <RouteSegmentCard
                                            key={ind}
                                            {...segment}
                                        />
                                    ))
                                }
                            </div>
                        )
                        : null
                }
            </div>
        </div>
    )
}

export default RouteChecker;